import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { HexTileMapKey } from '@/game/hexmap';
import type { SimState, TileVisibility } from '@/game/sim';
import type { Frame, FrameStore } from '@/game/view/frame';

/**
 * React's way into the match's per-tick `Frame` (game/view/frame.ts): the
 * match screen provides the store, and layers and panels subscribe to the
 * part they show with `useSimSelector`, re-rendering only when it changes,
 * instead of taking `getState`/`tickCount` props and re-deriving per tick.
 */
const FrameContext = createContext<FrameStore | null>(null);

export function FrameProvider({ store, children }: { store: FrameStore; children: ReactNode }) {
  return <FrameContext.Provider value={store}>{children}</FrameContext.Provider>;
}

export function useFrameStore(): FrameStore {
  const store = useContext(FrameContext);
  if (!store) throw new Error('useFrameStore needs a <FrameProvider>');
  return store;
}

/**
 * The part of the current frame `select` picks. Recomputed once per frame;
 * the component re-renders only when the result changes by `isEqual`
 * (`Object.is` by default), so a selector returning a new object or array
 * needs an `isEqual` that compares contents, or re-renders every tick.
 */
export function useSimSelector<T>(
  select: (frame: Frame) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  const store = useFrameStore();
  const cache = useRef<{ frame: Frame; select: (frame: Frame) => T; value: T } | null>(null);
  const snapshot = () => {
    const frame = store.get();
    const last = cache.current;
    if (last && last.frame === frame && last.select === select) return last.value;
    const value = select(frame);
    const kept = last && isEqual(last.value, value) ? last.value : value;
    cache.current = { frame, select, value: kept };
    return kept;
  };
  return useSyncExternalStore(store.subscribe, snapshot, snapshot);
}

const wholeFrame = (frame: Frame) => frame;

/** The whole current frame: re-renders every tick. */
export function useSimFrame(): Frame {
  return useSimSelector(wholeFrame);
}

/** The current fog: re-renders only when what the player sees or has explored changes. */
export function useFog() {
  return useSimSelector(fogOf);
}
const fogOf = (frame: Frame) => frame.fog;

export interface LiveReaders {
  /** The view state right now. */
  getState: () => SimState;
  /** Tile visibility (revealed attackers' tiles included) right now. */
  getTileVisibility: (key: HexTileMapKey) => TileVisibility;
}

/**
 * Stable functions reading the latest frame, for animation loops (R3F
 * `useFrame`) that run between renders and must see the sim as it is now.
 */
export function useLiveReaders(): LiveReaders {
  const store = useFrameStore();
  return useMemo(
    () => ({
      getState: () => store.get().view,
      getTileVisibility: (key) => store.get().fog.visibility(key),
    }),
    [store]
  );
}
